import { forwardRef } from 'react';
import './slider.css';

/**
 * Slider компонент для выбора числовых значений.
 *
 * `thumbIcon` — крупный бегунок с иконкой внутри (#1335): иконка говорит, что
 * двигаешь (часы — срок, валюта — сумма), и за круг в 32px проще ухватиться
 * пальцем. Нативный бегунок псевдоэлементом иконку не несёт, поэтому поверх
 * него рисуется круг-двойник, а сам `input range` остаётся на месте и
 * прозрачным: клавиатура, читалка и перетаскивание работают как у обычного
 * ползунка. Без `thumbIcon` разметка прежняя — один `<input>`.
 *
 * @param {import('react').ReactNode} [thumbIcon] — иконка внутри бегунка.
 */
const Slider = forwardRef(({
  className = '',
  min = 0,
  max = 100,
  step = 1,
  value = 0,
  onChange,
  style,
  thumbIcon,
  ...props
}, ref) => {
  const input = (
    <input
      ref={ref}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={onChange}
      className={`slider${thumbIcon ? ' slider--knob' : ''} ${className}`.trim()}
      style={{
        ['--slider-thumb-color']: 'var(--theme-primary, #2142FF)',
        ['--slider-thumb-hover-color']: 'var(--theme-primary-hover, #1C3ADD)',
        // caller style wins — allows per-instance thumb colour override
        ...style,
      }}
      {...props}
    />
  );

  if (!thumbIcon) return input;

  // Доля пути бегунка: по ней круг-двойник встаёт ровно над нативным.
  const span = Number(max) - Number(min);
  const share = span > 0 ? (Number(value) - Number(min)) / span : 0;
  return (
    <span
      className="slider-knob"
      // Динамическое значение, а не оформление: место бегунка меняется
      // с каждым шагом, CSS считает из него `left`.
      style={{ '--slider-share': Math.min(1, Math.max(0, share)) }}
    >
      {input}
      <span className="slider-knob__thumb" aria-hidden="true">
        {thumbIcon}
      </span>
    </span>
  );
});

Slider.displayName = 'Slider';

export { Slider };
